import React, { useMemo, useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, SendIcon, BanIcon } from 'lucide-react';
import { formatCurrency } from './pharmacyUtils';
type DraftLine = {
  medicineId: string;
  quantity: string;
  unitCost: string;
};
const newLine = (): DraftLine => ({
  medicineId: '',
  quantity: '',
  unitCost: ''
});
export function PurchaseOrders() {
  const {
    suppliers,
    medicines,
    purchaseOrders,
    createPurchaseOrder,
    updatePurchaseOrder,
    cancelPurchaseOrder,
    currentUser
  } = useData();
  const [showForm, setShowForm] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const manager = currentUser?.role === 'Pharmacy Manager';
  const orders = useMemo(() => [...purchaseOrders].sort((a, b) => new Date(b.dateReceived || b.createdAt).getTime() - new Date(a.dateReceived || a.createdAt).getTime()), [purchaseOrders]);
  const updateLine = (index: number, key: keyof DraftLine, value: string) => setLines(current => current.map((line, itemIndex) => itemIndex === index ? {
    ...line,
    [key]: value
  } : line));
  const cancelOrder = async (id: string) => {
    const reason = window.prompt('Enter the mandatory cancellation reason. The purchase order will remain in the audit register.');
    if (!reason) return;
    setCancellingId(id);
    const result = await cancelPurchaseOrder(id, reason);
    setCancellingId(null);
    if (!result.ok) setError(result.error || 'Unable to cancel purchase order.');
  };
  const save = (status: 'Draft' | 'Pending') => {
    const supplier = suppliers.find(item => item.id === supplierId);
    const parsedLines = lines.filter(line => line.medicineId && Number(line.quantity) > 0 && Number(line.unitCost) >= 0).map(line => {
      const medicine = medicines.find(item => item.id === line.medicineId)!;
      return {
        medicineId: medicine.id,
        medicineName: medicine.name,
        quantityOrdered: Number(line.quantity),
        quantityReceived: 0,
        unitCost: Number(line.unitCost)
      };
    });
    if (!supplier || !parsedLines.length) return setError('Select a supplier and add at least one valid medicine line.');
    createPurchaseOrder({
      supplierId: supplier.id,
      supplierName: supplier.name,
      status,
      orderedAt: new Date().toISOString(),
      expectedDate: expectedDate || undefined,
      notes: notes || undefined,
      lines: parsedLines,
      createdBy: currentUser?.id || '',
      kind: 'Planning'
    });
    setShowForm(false);
    setSupplierId('');
    setExpectedDate('');
    setNotes('');
    setLines([newLine()]);
    setError('');
  };
  return <div className="space-y-5" data-id="element-5267" data-mp="17epr5ibvcp4l">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-5268" data-mp="1cczljqprrlp5">
        <div data-id="element-5269" data-mp="1can7hon4tvhd">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-5270" data-mp="ydi4jxiptcqc">Purchase Orders</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-5271" data-mp="yfks8aweatrw">
            Planning orders and completed delivery receipts share one live
            procurement register.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-5272" data-mp="3j5k06kdfx2ui">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-5273" data-mp="76t3fldqwhht" /> New purchase order
        </Button>
      </div>
      {showForm && <section className="rounded-xl border border-teal-200 bg-teal-50/50 p-5" data-id="element-5274" data-mp="2fkpcc1h07yb">
          <div className="mb-4 flex justify-between" data-id="element-5275" data-mp="3tmdsvmjqkj60">
            <div data-id="element-5276" data-mp="3tmx7ata6gpfa">
              <h3 className="font-bold text-slate-900" data-id="element-5277" data-mp="3tkb3n4qequ2c">New purchase order</h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-5278" data-mp="3oovsikjsbc9m">
                Use Receive New Stock to turn delivered items into an audited
                receipt.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-id="element-5279" data-mp="l73sqjzepo0v">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="mb-3 text-sm text-red-600" data-id="element-5280" data-mp="30xymfoeo796a">
              {error}
            </p>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-5281" data-mp="2wvbu7me37m8k">
            <Select label="Supplier" value={supplierId} onChange={event => setSupplierId(event.target.value)} options={[{
          value: '',
          label: 'Select supplier'
        }, ...suppliers.filter(supplier => supplier.status === 'Active').map(supplier => ({
          value: supplier.id,
          label: supplier.name
        }))]} data-id="element-5282" data-mp="1cem57iazvd47" />
            <Input label="Expected date" type="date" value={expectedDate} onChange={event => setExpectedDate(event.target.value)} data-id="element-5283" data-mp="205rhjnhq7n9r" />
          </div>
          <div className="mt-4 overflow-x-auto" data-id="element-5284" data-mp="9r8xdgnemf42">
            <table className="min-w-[680px] w-full" data-id="element-5285" data-mp="9siua2iith94">
              <thead data-id="element-5286" data-mp="eq29b4n1ly24">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5287" data-mp="epitbtgydjqq">
                  <th className="pb-2" data-id="element-5288" data-mp="1i29ctcd12dwl">Medicine</th>
                  <th className="pb-2" data-id="element-5289" data-mp="1i32liivjrdhd">Quantity</th>
                  <th className="pb-2" data-id="element-5290" data-mp="1d36esbvgt7y4">Buying price</th>
                </tr>
              </thead>
              <tbody data-id="element-5291" data-mp="1r2z2dybp381v">
                {lines.map((line, index) => <tr key={index} data-id="element-5292" data-mp="1m3332cdy04sn" data-mpi={index}>
                    <td className="min-w-64 pb-2 pr-3" data-id="element-5293" data-mp="1m21pa05jl478">
                      <select aria-label="Medicine" value={line.medicineId} onChange={event => {
                  const medicine = medicines.find(item => item.id === event.target.value);
                  updateLine(index, 'medicineId', event.target.value);
                  if (!manager && medicine) updateLine(index, 'unitCost', String(medicine.buyingPrice));
                }} className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-5294" data-mp="20zu1ojfslch7">
                        <option value="" data-id="element-5295" data-mp="2ofw1i8afttjp">Select medicine</option>
                        {medicines.map(medicine => <option key={medicine.id} value={medicine.id} data-id="element-5296" data-mp="2tcw60ern2o8p">
                            {medicine.name}
                          </option>)}
                      </select>
                    </td>
                    <td className="pb-2 pr-3" data-id="element-5297" data-mp="2kflssnv9gp15">
                      <input aria-label="Quantity" type="number" min="1" value={line.quantity} onChange={event => updateLine(index, 'quantity', event.target.value)} className="w-28 rounded-md border border-slate-300 p-2 text-sm" data-id="element-5298" data-mp="25pecoesq7i3m" />
                    </td>
                    <td className="pb-2" data-id="element-5299" data-mp="2c67prt06sh6h">
                      <input aria-label="Buying price" type="number" min="0" step="0.01" value={line.unitCost} disabled={!manager} onChange={event => updateLine(index, 'unitCost', event.target.value)} className="w-32 rounded-md border border-slate-300 p-2 text-sm" data-id="element-5300" data-mp="2c79ciuqkes41" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => setLines(current => [...current, newLine()])} className="mt-2 text-sm font-semibold text-teal-700 hover:text-teal-900" data-id="element-5301" data-mp="1ehxxi5q7d7sk">
            + Add medicine line
          </button>
          <label className="mt-4 block text-sm font-medium text-slate-700" data-id="element-5302" data-mp="3m3o60v8jhd7n">
            Notes
            <textarea value={notes} onChange={event => setNotes(event.target.value)} rows={2} className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm" data-id="element-5303" data-mp="3q3q1unrlfcf0" />
          </label>
          <div className="mt-4 flex justify-end gap-2" data-id="element-5304" data-mp="32gqa7zy43cp8">
            <Button variant="outline" onClick={() => save('Draft')} data-id="element-5305" data-mp="32e413vvkop4w">
              Save draft
            </Button>
            <Button onClick={() => save('Pending')} className="bg-teal-600 hover:bg-teal-700" data-id="element-5306" data-mp="3ce4mrogwzhc9">
              <SendIcon className="mr-2 h-4 w-4" data-id="element-5307" data-mp="3h5wfo5njlz5k" /> Submit order
            </Button>
          </div>
        </section>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5308" data-mp="2jp76ts06dwmo">
        <div className="overflow-x-auto" data-id="element-5309" data-mp="2ompj9sdeitzf">
          <table className="min-w-[1050px] w-full divide-y divide-slate-100" data-id="element-5310" data-mp="2xk28886lr03i">
            <thead className="bg-slate-50" data-id="element-5311" data-mp="2xm69sjh13n0m">
              <tr data-id="element-5312" data-mp="32hvef17wnu3s">
                {['Order / receipt', 'Supplier & invoice', 'Received', 'Total cost', 'Status', ''].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5313" data-mp="2etc3sf1dv58j">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5314" data-mp="2ti8c8d7cqk9o">
              {orders.map(order => <Fragment key={order.id} data-id="element-5315" data-mp="1w2ujmyum9hys">
                  <tr data-id="element-5316" data-mp="1w21hkb6lrgmz">
                    <td className="px-5 py-3.5" data-id="element-5317" data-mp="1w2kqx3i6swpe">
                      <p className="font-semibold text-slate-900" data-id="element-5318" data-mp="211e753ouz1t9">
                        {order.orderNumber}
                      </p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5319" data-mp="i69nv5eg8vz9">
                        {order.kind === 'Receipt' ? 'Delivery receipt' : 'Procurement plan'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-5320" data-mp="47jrjrzgf0by">
                      <p data-id="element-5321" data-mp="3vdbbg9mn1gqb">{order.supplierName}</p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5322" data-mp="3ve4nnq4d8yfu">
                        {order.invoiceNumber ? `Invoice ${order.invoiceNumber}` : 'Invoice recorded on receipt'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-5323" data-mp="3hmpskkx6p6sf">
                      {order.dateReceived ? <>
                          <p data-id="element-5324" data-mp="3coxybk92h8l2">
                            {new Date(order.dateReceived).toLocaleDateString()}
                          </p>
                          <p className="mt-1 text-xs text-slate-500" data-id="element-5325" data-mp="2xzlh9vyatu66">
                            {order.receivedByName}
                          </p>
                        </> : order.expectedDate ? `Expected ${new Date(`${order.expectedDate}T00:00:00`).toLocaleDateString()}` : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-sm font-medium text-slate-700" data-id="element-5326" data-mp="3grxl6n4lp3if">
                      {formatCurrency(order.totalCost ?? order.lines.reduce((sum, line) => sum + line.quantityOrdered * line.unitCost, 0))}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-5327" data-mp="3i9npcm1tr5z">
                      <Status status={order.status} data-id="element-5328" data-mp="8buikvsci50y" />
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-5329" data-mp="1t0q2ewgxpnv1">
                      <div className="flex justify-end gap-3" data-id="element-5330" data-mp="1syvct4upx7km">
                        {order.status === 'Draft' && <button type="button" onClick={() => updatePurchaseOrder(order.id, {
                      status: 'Pending'
                    })} className="text-sm font-semibold text-teal-700 hover:text-teal-900" data-id="element-5331" data-mp="22qroxjtn2y7u">
                            Submit
                          </button>}
                        {manager && order.kind !== 'Receipt' && !['Received', 'Cancelled'].includes(order.status) && <button type="button" disabled={cancellingId === order.id} onClick={() => cancelOrder(order.id)} className="inline-flex items-center text-sm font-semibold text-red-700 hover:text-red-900 disabled:opacity-50" data-id="element-5332" data-mp="2qgm0ly282gqi">
                              <BanIcon className="mr-1 h-4 w-4" data-id="element-5333" data-mp="192j8x15kywwn" />
                              {cancellingId === order.id ? 'Cancelling…' : 'Cancel'}
                            </button>}
                        <button type="button" onClick={() => setExpandedId(expandedId === order.id ? null : order.id)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-id="element-5334" data-mp="1nvw7w7q5i9hg">
                          Items{' '}
                          {expandedId === order.id ? <ChevronUpIcon className="h-4 w-4" data-id="element-5335" data-mp="2bssh1a6qca8z" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-5336" data-mp="26tpmwksafofs" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === order.id && <tr className="bg-slate-50/60" data-id="element-5337" data-mp="2zh1yjzwwubub">
                      <td colSpan={6} className="px-5 py-4" data-id="element-5338" data-mp="2uja694scsnpl">
                        <table className="min-w-full text-sm" data-id="element-5339" data-mp="2ujtneja6j82k">
                          <thead className="text-left text-xs uppercase tracking-wide text-slate-500" data-id="element-5340" data-mp="2prs78m9w4x79">
                            <tr data-id="element-5341" data-mp="2lrrposrhw5vz">
                              <th className="pb-2" data-id="element-5342" data-mp="2lqq4vwkog512">Medicine</th>
                              <th className="pb-2" data-id="element-5343" data-mp="3i94m7v7yyse2">Batch</th>
                              <th className="pb-2" data-id="element-5344" data-mp="3di60uh84j43y">Quantity</th>
                              <th className="pb-2" data-id="element-5345" data-mp="3dgl7uw1gjt87">Buying price</th>
                              <th className="pb-2" data-id="element-5346" data-mp="1bkcbupq5m02d">Received</th>
                            </tr>
                          </thead>
                          <tbody data-id="element-5347" data-mp="1gj5qwml39z4n">
                            {order.lines.map(line => <tr key={`${order.id}-${line.medicineId}-${line.batchNumber || ''}`} data-id="element-5348" data-mp="11u12uorm50om">
                                <td className="py-1.5 font-medium text-slate-800" data-id="element-5349" data-mp="stl3nrhwkfpk">
                                  {line.medicineName}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5350" data-mp="xquu9ah12xez">
                                  {line.batchNumber || '—'}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5351" data-mp="nvjjt4cnkcd1">
                                  {line.quantityOrdered}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5352" data-mp="2i9aum76px2a2">
                                  {formatCurrency(line.buyingPrice ?? line.unitCost)}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5353" data-mp="gbifm2idijyr">
                                  {line.quantityReceived}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </td>
                    </tr>}
                </Fragment>)}
              {!orders.length && <tr data-id="element-5354" data-mp="z9bl9ckdt7zu">
                  <td colSpan={6} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-5355" data-mp="z9utu2o56k17">
                    No purchase orders or receipts yet.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}
function Status({
  status
}: {
  status: string;
}) {
  const colors: Record<string, string> = {
    Draft: 'bg-slate-100 text-slate-700',
    Pending: 'bg-blue-50 text-blue-700',
    'Partially Received': 'bg-amber-50 text-amber-700',
    Received: 'bg-emerald-50 text-emerald-700',
    Cancelled: 'bg-red-50 text-red-700'
  };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${colors[status] || 'bg-slate-100 text-slate-700'}`} data-id="element-5356" data-mp="2ox273ivgyz3k">
      {status}
    </span>;
}